import React, { useReducer, useState } from "react";
import { createEmptyShiftObject, getLanguage } from "../Managers/dataProvider";
import {
  EnumAlertDialogType,
  EnumPopupFunctions,
  PopupInfoModel,
  ShiftModel,
} from "../Managers/dataStructures";
import {
  postShiftOnServer,
  timeout,
  updateShiftInfo,
} from "../Managers/wepApiManager";
import { ActivityIndicator, Toast } from "../Managers/utilityComponents";
import { checkEntity } from "../Managers/utilityFunctions";

interface ShiftDetailProps {
  ftype: EnumPopupFunctions;
  shift?: ShiftModel;
  callBackHandler: (item: PopupInfoModel) => void;
  popupItem: PopupInfoModel;
}

export default function ShiftDetail(props: ShiftDetailProps) {
  const isEnglish = getLanguage();
  const defaultShift: ShiftModel =
    props.shift !== undefined && props.shift !== null
      ? props.shift
      : createEmptyShiftObject();

  const [shiftEle, setShiftEle] = React.useState<ShiftModel>(defaultShift);
  const [isActivityIndicator, setIsActivityIndicator] =
    useState<boolean>(false);
  const [toastText, setToastText] = useState<string>("");
  const [isToastVisible, setIsToastVisible] = useState<EnumAlertDialogType>(
    EnumAlertDialogType.NEUTRAL
  );
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  function onSave() {
    if (checkShiftInfo() === false) {
      return;
    }

    const cbItem: PopupInfoModel = {
      item: null,
      ftype: EnumPopupFunctions.CLOSE,
    };

    if (props.ftype === EnumPopupFunctions.ADD_SHIFT) {
      setIsActivityIndicator(true);
      postShiftOnServer(shiftEle, (isSuccess: boolean, shift_id: string) => {
        setIsActivityIndicator(false);
        if (isSuccess) {
          let tempShift = shiftEle;
          tempShift.shift_id = shift_id;
          setShiftEle(tempShift);

          if (props.popupItem.callback !== undefined) {
            props.popupItem.callback(shiftEle);
          }
          props.callBackHandler(cbItem);
        } else {
          showAlert(
            false,
            isEnglish
              ? "Error: Unable to acces server."
              : "Fehler: Serverzugriff fehlgeschlagen.",
            () => {}
          );
        }
      });
    } else if (props.ftype === EnumPopupFunctions.EDIT_SHIFT) {
      setIsActivityIndicator(true);
      updateShiftInfo(shiftEle, (isSuccess: boolean) => {
        setIsActivityIndicator(false);
        if (isSuccess) {
          if (props.popupItem.callback !== undefined) {
            props.popupItem.callback(shiftEle);
          }
          props.callBackHandler(cbItem);
        } else {
          showAlert(
            false,
            isEnglish
              ? "Error: Unable to acces server."
              : "Fehler: Serverzugriff fehlgeschlagen.",
            () => {}
          );
        }
      });
    }
  }

  function checkShiftInfo() {
    if (checkEntity(shiftEle.title) === false) {
      showAlert(
        false,
        isEnglish ? "Missing shift title." : "Fehlender Schichttitel",
        () => {}
      );
      return false;
    }
    if (checkEntity(shiftEle.start_time) === false) {
      showAlert(
        false,
        isEnglish
          ? "Invalid shift start time."
          : "Ungültige Schichtbeginnzeit.",
        () => {}
      );
      return false;
    }
    if (checkEntity(shiftEle.end_time) === false) {
      showAlert(
        false,
        isEnglish ? "Invalid shift end time." : "Ungültige Schichtendzeit.",
        () => {}
      );
      return false;
    }

    return true;
  }

  function onCancel() {
    const item_: PopupInfoModel = {
      item: null,
      ftype: EnumPopupFunctions.CLOSE,
    };
    props.callBackHandler(item_);
  }

  function onChangeTitle(value: string) {
    let tempShift: ShiftModel = shiftEle;
    tempShift.title = value;
    setShiftEle(tempShift);
  }

  function onChangeInfo(shiftEle: ShiftModel) {
    setShiftEle(shiftEle);
    forceUpdate();
  }

  async function showAlert(
    isPositive: boolean,
    text: string,
    callback: () => void
  ) {
    setToastText(text);
    if (isPositive) {
      setIsToastVisible(EnumAlertDialogType.POSITIVE);
    } else {
      setIsToastVisible(EnumAlertDialogType.NEGATIVE);
    }
    await timeout(2000);
    setIsToastVisible(EnumAlertDialogType.NEUTRAL);
    callback();
  }

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="flex justify-center mr-2 ml-2">
        <div className="">
          <div className="">
            <div className="">
              <div className="mt-2">
                <label
                  htmlFor="shift-title="
                  className="block text-sm font-semibold text-gray-900"
                >
                  {isEnglish ? "Shift Title" : "Schichtname"}
                </label>
                <div className="mt-2.5">
                  <input
                    type="string"
                    name="shift-title"
                    id="shift-title"
                    autoComplete="email"
                    onChange={(x) => onChangeTitle(x.currentTarget.value)}
                    defaultValue={props.shift?.title}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="flex justify-center mt-2">
                {TimePicker(shiftEle, onChangeInfo)}
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              {isActivityIndicator === true ? ActivityIndicator(1) : null}
              <button
                onClick={onCancel}
                type="button"
                className="text-sm font-semibold leading-6 text-gray-900 mx-5"
              >
                {isEnglish ? "Cancel" : "Abbrechen"}
              </button>
              <button
                type="submit"
                onClick={onSave}
                className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {isEnglish ? "Save" : "Speichern"}
              </button>
            </div>
            <div className="mt-4 mb-2">
              {isToastVisible === EnumAlertDialogType.POSITIVE ||
              isToastVisible === EnumAlertDialogType.NEGATIVE
                ? Toast(toastText, isToastVisible)
                : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimePicker(
  shift_ele: ShiftModel,
  onChangeInfo: (shift: ShiftModel) => void
) {
  const isEnglish = getLanguage();
  function onChangeStartAt(value: string) {
    let tempShift: ShiftModel = shift_ele;
    tempShift.start_time = value;
    onChangeInfo(tempShift);
  }

  function onChangeEndAt(value: string) {
    let tempShift: ShiftModel = shift_ele;
    tempShift.end_time = value;
    onChangeInfo(tempShift);
  }

  return (
    <div className="max-w-[16rem] mt-5 grid grid-cols-2 gap-4">
      <div>
        <label
          htmlFor="start-time"
          className="block mb-2 text-sm font-medium text-gray-900"
        >
          {isEnglish ? "Start time:" : "Arbeitsbeginn"}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 end-0 top-0 flex items-center pe-3.5 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500 white:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                fillRule="evenodd"
                d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4a1 1 0 1 0-2 0v4a1 1 0 0 0 .293.707l3 3a1 1 0 0 0 1.414-1.414L13 11.586V8Z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <input
            type="time"
            id="start-time"
            className="bg-gray-50 border leading-none border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 "
            value={shift_ele.start_time !== null ? shift_ele.start_time : ""}
            onChange={(x) => onChangeStartAt(x.currentTarget.value)}
            required
          />
        </div>
      </div>
      <div>
        <label
          htmlFor="end-time"
          className="block mb-2 text-sm font-medium text-gray-900"
        >
          {isEnglish ? "End time:" : "Arbeitsende"}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 end-0 top-0 flex items-center pe-3.5 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                fillRule="evenodd"
                d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4a1 1 0 1 0-2 0v4a1 1 0 0 0 .293.707l3 3a1 1 0 0 0 1.414-1.414L13 11.586V8Z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <input
            type="time"
            id="end-time"
            className="bg-gray-50 border leading-none border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            value={shift_ele.end_time !== null ? shift_ele.end_time : ""}
            onChange={(x) => onChangeEndAt(x.currentTarget.value)}
            required
          />
        </div>
      </div>
    </div>
  );
}
